import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  CloudArrowUpIcon,
  DocumentIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

// Import utilities and components
import CreditConfirmModal from './CreditConfirmModal';
import { 
  trackPDFUploadStarted,
  trackPDFUploadConfirmed,
  trackPDFConversionSuccess,
  trackPDFConversionError,
  trackUploadAbandonment,
  trackError 
} from '../../utils/analytics';

// Firebase imports
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { doc, addDoc, collection, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { storage, db } from '../../firebase/config';

// Import Day 5 additions
import { useTransactions, TRANSACTION_STATUS } from '../../hooks/useTransactions';

const FileUpload = () => {
  // Authentication and credits
  const { currentUser: user } = useAuth();
  const { userCredits: credits, getUserCredits, updateUserCredits } = useAuth();
  const refreshCredits = () => getUserCredits(user?.uid);

  // Day 5 addition - Transaction management
  const { updateTransactionStatus } = useTransactions();

  // Component state
  const [uploadState, setUploadState] = useState('idle'); // idle, confirming, uploading, complete, error
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [uploadTask, setUploadTask] = useState(null);
  const [currentTransactionId, setCurrentTransactionId] = useState(null);

  // Refs for cleanup
  const uploadTaskRef = useRef(null);
  const isMountedRef = useRef(true);
  const uploadStartTime = useRef(Date.now());

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (uploadTaskRef.current) {
        uploadTaskRef.current.cancel();
      }
    };
  }, []);

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Reset form to initial state
  const resetForm = useCallback(() => {
    // Track abandonment if user resets during process
    if (selectedFile && uploadState !== 'complete' && uploadState !== 'idle') {
      trackUploadAbandonment(uploadState, {
        fileName: selectedFile.name,
        creditsRequired: 1
      });
    }
    
    setSelectedFile(null);
    setUploadProgress(0);
    setError(null);
    setUploadState('idle');
    setShowConfirmModal(false);
    setUploadTask(null);
    setCurrentTransactionId(null);
    uploadTaskRef.current = null;
  }, [selectedFile, uploadState]);

  // Handle file selection and show confirmation
  const handleFileSelect = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setSelectedFile(file);
    setError(null);
    uploadStartTime.current = Date.now();

    try {
      // Basic file validation
      if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
        setError('Please select a PDF file.');
        setUploadState('error');
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB.');
        setUploadState('error');
        return;
      }
      
      // Track PDF upload started
      trackPDFUploadStarted({
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type || 'application/pdf',
        creditsRequired: 1
      });
      
      setUploadState('confirming');
      setShowConfirmModal(true);
    } catch (err) {
      console.error('Error validating file:', err);
      setError('Failed to validate file. Please try again.');
      setUploadState('error');
      
      // Track file validation error
      trackError({
        errorType: 'file_validation_error',
        errorMessage: err.message || 'Failed to validate file',
        component: 'FileUpload',
        additionalContext: {
          fileName: file.name,
          fileSize: file.size
        }
      });
    }
  }, []);

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop: handleFileSelect,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: false,
    disabled: uploadState === 'uploading' || !user
  });

  // Handle file rejections
  useEffect(() => {
    if (fileRejections.length > 0) {
      const rejection = fileRejections[0];
      if (rejection.errors[0]?.code === 'file-too-large') {
        setError('File too large. Maximum size is 10MB.');
      } else if (rejection.errors[0]?.code === 'file-invalid-type') {
        setError('Invalid file type. Only PDF files are supported.');
      } else {
        setError('File upload rejected. Please try again.');
      }
      setUploadState('error');
    }
  }, [fileRejections]);

  // Handle upload confirmation - Simple processing approach
  const handleConfirmUpload = useCallback(async (confirmData = {}) => {
    const { year, password } = confirmData;
    console.log('🚀 UPLOAD DEBUG: Starting upload process');
    console.log('🔐 UPLOAD DEBUG: Auth state:', {
      hasUser: !!user,
      userId: user?.uid,
      userEmail: user?.email,
      hasSelectedFile: !!selectedFile,
      fileName: selectedFile?.name,
      fileSize: selectedFile?.size,
      currentCredits: credits,
      userYear: year,
      hasPassword: !!password
    });

    if (!user || !selectedFile) {
      console.error('❌ UPLOAD DEBUG: Missing user or file', { user: !!user, selectedFile: !!selectedFile });
      return;
    }

    setShowConfirmModal(false);
    setUploadState('uploading');
    setUploadProgress(0);
    
    console.log('📋 UPLOAD DEBUG: UI state updated to uploading');
    
    // Track PDF upload confirmed
    trackPDFUploadConfirmed({
      fileName: selectedFile.name,
      creditsUsed: 1,
      userCreditsRemaining: credits - 1
    });

    try {
      // Generate unique filename
      const timestamp = Date.now();
      const sanitizedFileName = selectedFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const uniqueFileName = `${timestamp}_${sanitizedFileName}`;
      const storagePath = `user-uploads/${user.uid}/${uniqueFileName}`;

      console.log('📁 UPLOAD DEBUG: File paths generated:', {
        timestamp,
        sanitizedFileName,
        uniqueFileName,
        storagePath
      });

      // Create transaction record with simple 1-credit processing fee
      const transactionData = {
        userId: user.uid,
        fileName: selectedFile.name,
        originalFileName: selectedFile.name,
        fileSize: selectedFile.size,
        creditsUsed: 1, // Simple 1-credit processing fee
        status: TRANSACTION_STATUS.UPLOADING,
        storagePath: storagePath,
        createdAt: new Date(),
        progress: 0,
        uploadStarted: new Date(),
        ...(year && { userYear: year }), // Include user year if provided
        ...(password && { pdfPassword: password }) // Include password if provided (will be cleared after processing)
      };

      console.log('💾 UPLOAD DEBUG: Creating transaction document:', transactionData);

      // Add transaction to Firestore
      const transactionRef = await addDoc(collection(db, 'transactions'), transactionData);
      const transactionId = transactionRef.id;
      setCurrentTransactionId(transactionId);

      console.log('✅ UPLOAD DEBUG: Transaction created successfully:', {
        transactionId,
        transactionRef: transactionRef.path
      });

      // Set up real-time listener for this specific transaction
      console.log('👂 UPLOAD DEBUG: Setting up transaction listener for:', transactionId);
      
      const unsubscribe = onSnapshot(
        doc(db, 'transactions', transactionId), 
        async (doc) => {
          console.log('🔄 UPLOAD DEBUG: Transaction listener fired');
          if (doc.exists()) {
            const data = doc.data();
            console.log('📄 UPLOAD DEBUG: Transaction update received:', {
              transactionId,
              status: data.status,
              progress: data.progress,
              errorMessage: data.errorMessage,
              downloadUrl: data.downloadUrl,
              allData: data
            });
            
            // Update UI based on transaction status
            if (data.status === 'completed') {
              console.log('🎉 UPLOAD DEBUG: Transaction completed successfully');
              setUploadState('complete');
              setUploadProgress(100);
              
              // Track successful conversion
              const processingTime = Math.floor((Date.now() - uploadStartTime.current) / 1000);
              trackPDFConversionSuccess({
                fileName: selectedFile.name,
                creditsUsed: data.creditsUsed || 1,
                processingTimeSeconds: processingTime,
                outputFormat: 'csv',
                transactionId: transactionId
              });
              
              // Credits are handled by backend processing - no frontend deduction needed
              console.log('💳 UPLOAD DEBUG: Backend handled credit processing:', data.creditsUsed || 1, 'credits');
              
              // Refresh credits after successful processing
              refreshCredits();
              
              console.log('✅ UPLOAD DEBUG: Processing completed! CSV available at:', data.downloadUrl);
            } else if (data.status === 'error') {
              console.error('❌ UPLOAD DEBUG: Transaction failed with error:', {
                status: data.status,
                errorMessage: data.errorMessage,
                creditsRefunded: data.creditsRefunded
              });
              setUploadState('error');
              setError(data.errorMessage || 'Processing failed');
              
              // Track conversion error
              trackPDFConversionError({
                fileName: selectedFile.name,
                errorType: 'processing_failed',
                errorMessage: data.errorMessage || 'Processing failed',
                creditsRefunded: data.creditsRefunded || 0
              });
            } else if (data.status === 'processing') {
              console.log('⚙️ UPLOAD DEBUG: Transaction now processing');
              setUploadState('uploading'); // Keep showing as uploading during processing
              setUploadProgress(75); // Show that we're processing
            }
          } else {
            console.error('❌ UPLOAD DEBUG: Transaction document does not exist:', transactionId);
          }
        },
        (error) => {
          console.error('❌ UPLOAD DEBUG: Transaction listener error:', error);
        }
      );

      // Store unsubscribe function for cleanup
      uploadTaskRef.current = { cancel: unsubscribe };
      
      // Create storage reference
      console.log('🗂️ UPLOAD DEBUG: Creating storage reference:', storagePath);
      const storageRef = ref(storage, storagePath);
      
      // Create upload task
      console.log('📤 UPLOAD DEBUG: Starting storage upload');
      const uploadTask = uploadBytesResumable(storageRef, selectedFile);
      setUploadTask(uploadTask);

      // Monitor upload progress
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          if (!isMountedRef.current) return;
          
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          const roundedProgress = Math.round(progress);
          
          console.log('📊 UPLOAD DEBUG: Storage upload progress:', {
            bytesTransferred: snapshot.bytesTransferred,
            totalBytes: snapshot.totalBytes,
            progress: roundedProgress,
            state: snapshot.state
          });
          
          // Only update progress if we're still uploading (not processing)
          if (roundedProgress < 100) {
            setUploadProgress(roundedProgress);
          }

          // DAY 5 UPDATE: Update transaction progress in real-time
          updateTransactionStatus(transactionId, TRANSACTION_STATUS.UPLOADING, {
            progress: roundedProgress
          });
        },
        (error) => {
          if (!isMountedRef.current) return;
          
          console.error('❌ UPLOAD DEBUG: Storage upload error:', {
            code: error.code,
            message: error.message,
            serverResponse: error.serverResponse,
            customMetadata: error.customMetadata
          });
          
          let errorMessage = 'Upload failed. Please try again.';
          
          switch (error.code) {
            case 'storage/unauthorized':
              console.error('🔐 UPLOAD DEBUG: FOUND PERMISSION DENIED ERROR - This is likely the source!');
              errorMessage = 'Permission denied. Please log in and try again.';
              break;
            case 'storage/canceled':
              errorMessage = 'Upload was canceled.';
              break;
            case 'storage/quota-exceeded':
              errorMessage = 'Storage quota exceeded. Please contact support.';
              break;
            default:
              errorMessage = `Upload failed: ${error.message}`;
          }
          
          console.error('❌ UPLOAD DEBUG: Setting error state:', errorMessage);
          setError(errorMessage);
          setUploadState('error');
          
          // Track upload error
          trackError({
            errorType: 'storage_upload_error',
            errorMessage: errorMessage,
            component: 'FileUpload',
            additionalContext: {
              fileName: selectedFile.name,
              storageErrorCode: error.code
            }
          });
          
          // Cleanup listener
          if (uploadTaskRef.current?.cancel) {
            uploadTaskRef.current.cancel();
          }

          // DAY 5 UPDATE: Update transaction status to error
          updateTransactionStatus(transactionId, TRANSACTION_STATUS.ERROR, {
            errorMessage: errorMessage,
            uploadFailed: new Date()
          });
        },
        async () => {
          if (!isMountedRef.current) return;
          
          console.log('🎯 UPLOAD DEBUG: Storage upload completed successfully!');
          
          try {
            // Upload completed successfully - use retry logic for getDownloadURL
            console.log('📥 UPLOAD DEBUG: Getting download URL with retry logic...');
            
            const getDownloadURLWithRetry = async (ref, retries = 2) => {
              for (let i = 0; i <= retries; i++) {
                try {
                  if (i > 0) {
                    // Refresh auth token before retry
                    console.log(`🔄 UPLOAD DEBUG: Refreshing auth token (retry ${i + 1}/${retries + 1})`);
                    await user.getIdToken(true);
                  }
                  console.log(`📥 UPLOAD DEBUG: Attempting getDownloadURL (attempt ${i + 1}/${retries + 1})`);
                  return await getDownloadURL(ref);
                } catch (error) {
                  console.error(`❌ UPLOAD DEBUG: getDownloadURL attempt ${i + 1} failed:`, error);
                  if (error.code === 'storage/unauthorized' && i < retries) {
                    console.log(`⏳ UPLOAD DEBUG: Retrying getDownloadURL due to unauthorized error...`);
                    continue;
                  }
                  throw error;
                }
              }
            };

            const downloadURL = await getDownloadURLWithRetry(uploadTask.snapshot.ref);
            
            console.log('✅ UPLOAD DEBUG: Got download URL:', downloadURL);
            console.log('🔄 UPLOAD DEBUG: Updating transaction to processing status');

            // DAY 5 UPDATE: Update transaction to processing status
            await updateTransactionStatus(transactionId, TRANSACTION_STATUS.PROCESSING, {
              uploadUrl: downloadURL,
              progress: 100,
              uploadCompleted: new Date(),
              processingStarted: new Date()
            });

            console.log('📈 UPLOAD DEBUG: Transaction updated to processing, waiting for Cloud Function trigger');

            // Set upload progress to 100% and show processing message
            setUploadProgress(100);
            
            console.log('⏳ UPLOAD DEBUG: Now waiting for Cloud Function to process the file...');
            // Note: Don't set uploadState to 'complete' here
            // Wait for the real-time listener to detect 'completed' status
            
          } catch (error) {
            console.error('❌ UPLOAD DEBUG: Error in upload completion handler:', {
              message: error.message,
              code: error.code,
              stack: error.stack
            });
            
            // Handle different error types intelligently
            if (error.code === 'storage/unauthorized' || error.message?.includes('unauthorized')) {
              console.warn('⚠️ UPLOAD DEBUG: Download URL generation failed, but upload succeeded');
              console.log('🔄 UPLOAD DEBUG: Continuing processing without download URL...');
              
              // Upload succeeded, just download URL failed - don't show error to user
              // Still update transaction to processing, let function handle completion
              try {
                await updateTransactionStatus(transactionId, TRANSACTION_STATUS.PROCESSING, {
                  uploadCompleted: new Date(),
                  processingStarted: new Date()
                });
                setUploadProgress(100);
                console.log('✅ UPLOAD DEBUG: Transaction marked as processing despite download URL error');
                return; // Don't show error to user
              } catch (statusUpdateError) {
                console.error('❌ UPLOAD DEBUG: Failed to update transaction status:', statusUpdateError);
              }
            }
            
            // For other errors, show error to user
            console.error('❌ UPLOAD DEBUG: Actual upload completion failure');
            setError('Upload completed but failed to process. Please contact support.');
            setUploadState('error');

            // Cleanup listener
            if (uploadTaskRef.current?.cancel) {
              uploadTaskRef.current.cancel();
            }

            // DAY 5 UPDATE: Update transaction status to error
            if (transactionId) {
              updateTransactionStatus(transactionId, TRANSACTION_STATUS.ERROR, {
                errorMessage: 'Post-upload processing failed',
                processingFailed: new Date(),
                errorCode: error.code,
                fullError: error.message
              });
            }
          }
        }
      );
      
    } catch (error) {
      console.error('❌ UPLOAD DEBUG: Fatal error starting upload process:', {
        message: error.message,
        code: error.code,
        name: error.name,
        stack: error.stack
      });
      setError('Failed to start upload. Please try again.');
      setUploadState('error');

      // DAY 5 UPDATE: Update transaction status if it was created
      if (currentTransactionId) {
        updateTransactionStatus(currentTransactionId, TRANSACTION_STATUS.ERROR, {
          errorMessage: 'Failed to start upload',
          uploadFailed: new Date()
        });
      }
    }
  }, [user, selectedFile, updateTransactionStatus, refreshCredits]);

  // Handle modal cancellation
  const handleCancelUpload = useCallback(() => {
    // Track upload abandonment
    if (selectedFile) {
      trackUploadAbandonment('confirming', {
        fileName: selectedFile.name,
        creditsRequired: 1
      });
    }
    
    setShowConfirmModal(false);
    setUploadState('idle');
  }, [selectedFile]);

  // Handle retry after error
  const handleRetry = useCallback(() => {
    resetForm();
  }, [resetForm]);

  // Cancel ongoing upload
  const handleCancelOngoingUpload = useCallback(() => {
    if (uploadTaskRef.current) {
      uploadTaskRef.current.cancel();
      uploadTaskRef.current = null;
    }
    
    // DAY 5 UPDATE: Update transaction status to cancelled
    if (currentTransactionId) {
      updateTransactionStatus(currentTransactionId, TRANSACTION_STATUS.ERROR, {
        errorMessage: 'Upload cancelled by user',
        uploadCancelled: new Date()
      });
    }
    
    resetForm();
  }, [currentTransactionId, updateTransactionStatus, resetForm]);

  // Render different states
  const renderUploadArea = () => {
    if (uploadState === 'uploading') {
      return (
        <div className="text-center py-8">
          <CloudArrowUpIcon className="h-12 w-12 text-blue-500 mx-auto mb-4 animate-pulse" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Uploading...</h3>
          <p className="text-gray-600 mb-4">{selectedFile?.name}</p>
          
          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div 
              className="bg-blue-500 h-3 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="text-sm text-gray-500 mb-4">{uploadProgress}% complete</p>
          
          <button
            onClick={handleCancelOngoingUpload}
            className="text-red-600 hover:text-red-800 text-sm font-medium"
          >
            Cancel Upload
          </button>
        </div>
      );
    }

    if (uploadState === 'complete') {
      return (
        <div className="text-center py-8">
          <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Complete!</h3>
          <p className="text-gray-600 mb-4">
            Your file is being processed. Check the transaction history below for updates.
          </p>
          <button
            onClick={resetForm}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            Upload Another File
          </button>
        </div>
      );
    }

    if (uploadState === 'error') {
      return (
        <div className="text-center py-8">
          <ExclamationCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Failed</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={handleRetry}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            Try Again
          </button>
        </div>
      );
    }

    if (uploadState === 'analyzing') {
      return (
        <div className="text-center py-8">
          <ArrowPathIcon className="h-12 w-12 text-blue-500 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Analyzing File...</h3>
          <p className="text-gray-600">Calculating credits required</p>
        </div>
      );
    }

    // Default idle state
    return (
      <div 
        {...getRootProps()} 
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors duration-200
          ${isDragActive 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
          }
          ${!user ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />
        
        <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        
        {!user ? (
          <>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Please log in to upload files</h3>
            <p className="text-gray-600">You need to be logged in to convert bank statements.</p>
          </>
        ) : isDragActive ? (
          <>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Drop your PDF here</h3>
            <p className="text-gray-600">Release to upload your bank statement</p>
          </>
        ) : (
          <>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Upload Bank Statement</h3>
            <p className="text-gray-600 mb-4">
              Drag and drop your PDF bank statement here, or click to browse
            </p>
            <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
              <span>• PDF files only</span>
              <span>• Max 10MB</span>
              <span>• 1 Credit per page</span>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Credit balance display */}
      {user && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CurrencyDollarIcon className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-green-800 font-medium">
                Available Credits: {credits || 0}
              </span>
            </div>
            {(credits || 0) < 5 && (
              <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                Buy More Credits
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main upload area */}
      {renderUploadArea()}

      {/* Credit confirmation modal */}
      {showConfirmModal && selectedFile && (
        <CreditConfirmModal
          isOpen={showConfirmModal}
          onClose={handleCancelUpload}
          onConfirm={handleConfirmUpload}
          fileName={selectedFile?.name}
          userCredits={credits || 0}
        />
      )}
    </div>
  );
};

export default FileUpload;